/**
 * Copy text to the clipboard, trying every path a browser offers.
 *
 * 1. The async Clipboard API (navigator.clipboard.writeText). Missing on plain-http pages and in some
 *    embedded browsers (in-app webviews), and rejected when permission is denied or the page is not
 *    focused.
 * 2. The legacy path: a hidden <textarea>, select it, document.execCommand('copy'). Works in most of
 *    the places (1) does not.
 *
 * Returns which path worked, or null when both failed (the caller must then show the text so the
 * user can copy it by hand: see useCopyToClipboard).
 */
export type CopyMethod = 'clipboard-api' | 'exec-command';

export async function copyTextToClipboard(text: string): Promise<CopyMethod | null> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return 'clipboard-api';
    }
  } catch {
    // fall through to the legacy path
  }
  return copyWithExecCommand(text) ? 'exec-command' : null;
}

function copyWithExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) return false;
  const active = document.activeElement as HTMLElement | null;
  // Inside a modal dialog the focus trap pulls focus back out of anything appended to <body>, which
  // empties the selection before the copy runs. Put the textarea inside the dialog instead.
  const container = (active?.closest('[role="dialog"], [role="alertdialog"]') as HTMLElement | null) ?? document.body;
  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.tabIndex = -1;
  // Off-screen but rendered: some browsers refuse to copy from display:none / visibility:hidden.
  Object.assign(textarea.style, {
    position: 'fixed',
    top: '0',
    left: '-9999px',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
  });
  container.appendChild(textarea);
  let ok = false;
  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length); // iOS Safari ignores select() alone
    ok = typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    textarea.remove();
    try {
      active?.focus?.({ preventScroll: true });
      if (previousRange && selection) {
        selection.removeAllRanges();
        selection.addRange(previousRange);
      }
    } catch {
      /* restoring focus/selection is best effort */
    }
  }
  return ok;
}
